import { useTranslation } from 'react-i18next';
import { trDisplay, translateDescriptor } from '../../i18n';
import React, { useEffect, useRef, useState } from 'react';
import { Activity, Check, ChevronDown, Clock3, CircleAlert, X } from 'lucide-react';
import { swrActivityMonitor, formatSWRRemaining } from '../../lib/swrActivityMonitor';
import { SWRHelp } from './SWRHelp';
import './SWRCalculationStatus.css';

const live = job => !['complete', 'cancelled', 'error'].includes(job.status);
const waiting = job => ['queued', 'planned'].includes(job.status);
const statusLabel = job => ({ queued: 'In coda', planned: 'In attesa', preparing: 'Preparazione',
  finalizing: 'Salvataggio finale', cancelling: 'Interruzione', complete: 'Completato', cancelled: 'Interrotto', error: 'Non completato' }[job.status] || 'In corso');

function timeLabel(job) {
  if (job.recalibrating && live(job)) return 'Stima in aggiornamento';
  if (waiting(job)) {
    const finish = formatSWRRemaining(job.finishMs);
    return finish ? `Pronto tra circa ${finish}` : job.status === 'planned' ? 'Tempo disponibile dopo l’avvio' : 'Stima dell’attesa in preparazione';
  }
  if (!live(job)) return statusLabel(job);
  if (job.status === 'finalizing') return 'Conferma del salvataggio in corso';
  if (job.status === 'cancelling') return 'Conservo il lavoro già completato';
  const time = formatSWRRemaining(job.remainingMs);
  return time ? time === 'pochi secondi' ? 'Mancano pochi secondi' : `Mancano circa ${time}` : 'Stima in preparazione';
}

function Progress({ job }) {
  useTranslation();
  const indeterminate = ['preparing', 'finalizing', 'cancelling'].includes(job.status);
  const percent = waiting(job) ? 0 : job.percent;
  return <div className={`swr-calculation-bar ${indeterminate ? 'swr-calculation-bar--indeterminate' : ''}`}
    role="progressbar" aria-label={`${trDisplay(job.label)} · ${trDisplay(job.context)}`} aria-valuemin={0} aria-valuemax={100}
    aria-valuenow={indeterminate ? undefined : percent} aria-valuetext={`${trDisplay(statusLabel(job))}${indeterminate ? '' : `, ${Math.round(percent)}%`}`}>
    <span style={indeterminate ? undefined : { width: `${percent}%` }} />
  </div>;
}

export default function SWRCalculationStatus({ monitor = swrActivityMonitor, onNavigate = () => {} }) {
  useTranslation();
  const [jobs, setJobs] = useState(() => monitor.snapshot());
  const [view, setView] = useState('preview');
  const lastRefresh = useRef(0);
  useEffect(() => {
    const refresh = immediate => {
      if (!immediate && Date.now() - lastRefresh.current < 1000) return;
      lastRefresh.current = Date.now(); setJobs(monitor.snapshot());
    };
    const unsubscribe = monitor.subscribe(refresh); refresh(true);
    const visibility = () => monitor.visibility(document.visibilityState === 'hidden');
    visibility();
    document.addEventListener('visibilitychange', visibility);
    return () => { unsubscribe(); document.removeEventListener('visibilitychange', visibility); };
  }, [monitor]);
  const hasJobs = jobs.length > 0;
  useEffect(() => {
    if (!hasJobs) return undefined;
    const timer = setInterval(() => {
      if (Date.now() - lastRefresh.current >= 1000) { lastRefresh.current = Date.now(); setJobs(monitor.snapshot()); }
    }, 1000);
    return () => clearInterval(timer);
  }, [hasJobs, monitor]);
  if (!hasJobs) return null;

  const active = jobs.filter(j => live(j) && !waiting(j));
  const queued = jobs.filter(waiting);
  const errors = jobs.filter(j => j.status === 'error');
  const completed = jobs.filter(j => !live(j) && j.status !== 'error');
  const ordered = [...active, ...queued, ...errors, ...(active.length || queued.length || errors.length ? [] : completed)];
  const collapsed = view === 'collapsed';
  const visible = collapsed ? active.slice(0, 2) : view === 'all' ? ordered : ordered.slice(0, 3);
  const allDone = !active.length && !queued.length;
  const heading = allDone ? errors.length ? 'Alcuni calcoli non sono completati' : completed.some(j => j.status === 'cancelled') ? 'Calcoli interrotti' : 'Calcoli completati' : 'Calcoli SWR';
  const summary = `${active.length} in corso · ${queued.length} in attesa${errors.length ? ` · ${errors.length} con errore` : ''}`;
  return <section className="swr-calculation-status" aria-label={trDisplay("Stato dei calcoli SWR")} data-testid="swr-calculation-status">
    <div className="swr-calculation-status__inner">
      <div className="swr-calculation-status__heading">
        <div className="swr-calculation-status__title">
          {trDisplay(errors.length ? <CircleAlert size={16} className="text-destructive" /> : allDone ? <Check size={16} /> : <Activity size={16} className="swr-calculation-accent" />)}
          <strong>{trDisplay(heading)}</strong>
          <span className="swr-calculation-status__counts">{trDisplay(!allDone && summary)}</span>
          <SWRHelp topic="calculationProgress" side="bottom" />
        </div>
        <button type="button" className="swr-calculation-status__toggle" aria-expanded={!collapsed}
          aria-controls="swr-calculation-list" onClick={() => setView(collapsed ? 'preview' : 'collapsed')}>
          {trDisplay(collapsed ? 'Espandi' : 'Comprimi')}<ChevronDown size={16} className={!collapsed ? 'rotate-180' : ''} />
        </button>
      </div>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">{trDisplay(heading)}. {trDisplay(!allDone && summary)}</div>
      <div id="swr-calculation-list" className={`swr-calculation-status__list ${collapsed ? 'swr-calculation-status__list--collapsed' : ''} ${view === 'all' ? 'swr-calculation-status__list--all' : ''}`}>
        {trDisplay(visible.map(job => <div key={job.id} className="swr-calculation-job" data-state={job.status} data-testid={`swr-activity-${job.id}`}>
          <div className="swr-calculation-job__identity">
            <span className="swr-calculation-job__name">{trDisplay(job.label)}</span>
            <span className="swr-calculation-job__context">{trDisplay(job.context)}</span>
          </div>
          <div className="swr-calculation-job__track"><Progress job={job} /></div>
          <span className="swr-calculation-job__percent">{trDisplay(['preparing', 'finalizing', 'cancelling'].includes(job.status) ? '—' : `${Math.round(waiting(job) ? 0 : job.percent)}%`)}</span>
          <div className="swr-calculation-job__time"><span>{trDisplay(timeLabel(job))}</span>
            {trDisplay(!collapsed && <small>{trDisplay(waiting(job) ? job.status === 'planned' ? 'Attende l’analisi precedente' : `In coda${job.waitMs !== null ? ` · attesa circa ${formatSWRRemaining(job.waitMs)}` : ''}${job.remainingMs !== null ? ` · durata circa ${formatSWRRemaining(job.remainingMs)}` : ''}` : job.phaseMessage ? translateDescriptor(job.phaseMessage) : job.phase || statusLabel(job))}</small>)}
          </div>
          {trDisplay(job.status === 'error' && <div className="swr-calculation-job__error">
            <span role="alert">{trDisplay(job.error || 'Il lavoro non è riuscito. Rivedi i dati e riprova dalla sezione originale.')}</span>
            <button type="button" onClick={() => onNavigate(job.kind)}>{trDisplay("Vedi dettaglio")}</button>
            <button type="button" aria-label={trDisplay(`Chiudi avviso ${job.label}`)} onClick={() => monitor.dismiss(job.id)}><X size={16} /></button>
          </div>)}
        </div>))}
        {trDisplay(collapsed && (!active.length || queued.length > 0 || errors.length > 0) && !allDone && <p className="swr-calculation-status__compact-queue"><Clock3 size={12} />{trDisplay(queued.length)}{trDisplay(" in attesa")}{trDisplay(errors.length ? ` · ${errors.length} con errore` : '')}{trDisplay(" · Espandi per i dettagli")}</p>)}
      </div>
      {trDisplay(!collapsed && view !== 'all' && ordered.length > 3 && <button type="button" className="swr-calculation-status__more" onClick={() => setView('all')}>{trDisplay("Mostra tutti · altri ")}{trDisplay(ordered.length - 3)}<ChevronDown size={14} /></button>)}
      {trDisplay(view === 'all' && ordered.length > 3 && <button type="button" className="swr-calculation-status__more" onClick={() => setView('preview')}>{trDisplay("Mostra i primi tre")}</button>)}
      {trDisplay(collapsed && allDone && errors.length > 0 && <button className="swr-calculation-status__more" onClick={() => setView('preview')}>{trDisplay("Vedi gli avvisi")}</button>)}
    </div>
  </section>;
}
